import { jwtDecode } from 'jwt-decode';
import { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';

import { API_BASE_URL } from '../utils/env';

const useUsers = () => {
  const [USERLIST, setUsers] = useState([]);
  const [loader, setLoader] = useState(true);
  const [error, setError] = useState(null);

  // DEACTIVATED MEMBERS STATE
  const [deactivatedMembers, setDeactivatedMembers] = useState([]);
  const [deactivatedMembersLoader, setDeactivatedMembersLoader] = useState(true);
  const [deactivatedMembersError, setDeactivatedMembersError] = useState(null);

  // PARISH REPORT STATE
  const [parishReport, setParishReport] = useState([]);
  const [parishReportLoader, setParishReportLoader] = useState(true);
  const [parishReportError, setParishReportError] = useState(null);

  // ATTENDANCE REPORT STATE
  const [attendance, setAttendance] = useState([]);
  const [attendanceLoader, setAttendanceLoader] = useState(true);
  const [attendanceError, setAttendanceError] = useState(null);

  const { userInfo } = useSelector((state) => state.auth);
  const decodedToken = jwtDecode(userInfo.token);
  const parishId = decodedToken.parishId;

  // token to be used in headers for the token-based authentication
  const token = userInfo.token;

  // use effect to fetch members
  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/Member/GetMemberByParishId?ParishId=${parishId}`, {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        });
        if (!response.ok) {
          // Attempt to capture the error message from the response
          const errorResponse = await response.json();
          const errorMessage = errorResponse.error;
          throw new Error(errorMessage);
        }
        const responseData = await response.json();

        if (responseData.success && responseData.data === null) {
          // Handle the case where the API returns null for data
          setUsers([]);
          setLoader(false);
          setError(responseData.message);
        } else {
          // Handle the case where data is not null
          setUsers(responseData.data);
          setLoader(false);
          setError(null);
          // console.log(responseData.data);
        }
      } catch (error) {
        setLoader(false);
        setError(error.message);
        console.log(error);
      }
    };

    fetchUsers();
  }, [parishId, token]);

  // use effect to fetch deactivated members
  useEffect(() => {
    const fetchDeactivatedMembers = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/Member/GetDeactivatedMemberByParishId?parishId=${parishId}`, {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        });
        if (!response.ok) {
          // Attempt to capture the error message from the response
          const errorResponse = await response.json();
          const errorMessage = errorResponse.error;
          throw new Error(errorMessage);
        }
        const responseData = await response.json();

        if (responseData.success && responseData.data === null) {
          // Handle the case where the API returns null for data
          setDeactivatedMembers([]);
          setDeactivatedMembersLoader(false);
          setDeactivatedMembersError(responseData.message);
        } else {
          // Handle the case where data is not null
          setDeactivatedMembers(responseData.data);
          setDeactivatedMembersLoader(false);
          setDeactivatedMembersError(null);
          // console.log(responseData.data);
        }
      } catch (error) {
        setDeactivatedMembersLoader(false);
        setDeactivatedMembersError(error.message);
        console.log(error);
      }
    };

    fetchDeactivatedMembers();
  }, [parishId, token]);

  // use effect to fetch parish report
  useEffect(() => {
    const fetchParishReport = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/Report/GetParishReport?parishId=${parishId}`, {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        });
        if (!response.ok) {
          // Attempt to capture the error message from the response
          const errorResponse = await response.json();
          const errorMessage = errorResponse.error;
          throw new Error(errorMessage);
        }
        const responseData = await response.json();

        if (responseData.success && responseData === null) {
          // Handle the case where the API returns null for data
          setParishReport([]);
          setParishReportLoader(false);
          setParishReportError(responseData.message);
        } else {
          // Handle the case where data is not null
          setParishReport(responseData);
          setParishReportLoader(false);
          setParishReportError(null);
          console.log(responseData.newMemberData.totalMemberCount);
        }
      } catch (error) {
        setParishReportLoader(false);
        setParishReportError(error.message);
        console.log(error);
      }
    };

    fetchParishReport();
  }, [parishId, token]);

  useEffect(() => {
    const fetchAttendance = async () => {
      const currentDate = new Date();
      const presentDate = currentDate.toISOString().split('T')[0];

      const requestBody = {
        parishId,
        startDate: presentDate,
        endDate: presentDate,
        page: 2,
        pageSize: 2,
      };

      try {
        const response = await fetch(`${API_BASE_URL}/Attendance/GetAttendance`, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(requestBody),
        });

        if (!response.ok) {
          const errorResponse = await response.json();
          throw new Error(errorResponse.error || 'Failed to fetch attendance');
        }

        const responseData = await response.json();

        if (responseData.success && responseData.data === null) {
          setAttendance([]);
          setAttendanceLoader(false);
          setAttendanceError(responseData.message);
        } else {
          setAttendance(responseData.data);
          console.log(responseData.data);
          setAttendanceLoader(false);
          setAttendanceError(null);
        }
      } catch (error) {
        setAttendanceLoader(false);
        setAttendanceError(error.message);
        console.log(error);
      }
    };

    if (parishId && token) {
      fetchAttendance();
    }
  }, [parishId, token]);

  return {
    USERLIST,
    setUsers,
    loader,
    error,
    setError,
    deactivatedMembers,
    deactivatedMembersError,
    deactivatedMembersLoader,
    setDeactivatedMembers,
    setDeactivatedMembersError,
    parishReport,
    parishReportError,
    parishReportLoader,
    attendance,
    attendanceLoader,
    attendanceError,
  };
};

export default useUsers;
